import { useEffect, useMemo, useState } from 'react'
import { assetById, assets, defaultInputValues, methodology } from './data/catalog'
import { calculate, formatIndicator, formatMoney, getPipeMaterials } from './calculation'
import { addHistory, clearHistory, isHistoryOutdated, refreshOutdatedHistory, removeHistory } from './history'
import { copyResult, exportCsv, exportXlsx } from './export'
import type { AssetDefinition, CalculationInput, CalculationResult, HistoryEntry, NumericPoint, Sector } from './types'

const sectors: Sector[] = ['Vodovody', 'Kanalizace', 'Přepočet']

const simplifiedArticleVii = 'Zpracovatelé Vybraných údajů majetkové evidence vodovodů a kanalizací a Plánů financování obnovy mohou použít v případech, že výpočet provedli podle metodického pokynu Ministerstva zemědělství pro výpočet pořizovací ceny objektů podle orientačních ukazatelů do Vybraných údajů majetkové evidence vodovodů a kanalizací, který byl vydán dne 24. 9. 2020 pod čj. 14000/2020-15132-1, a nedošlo ke změnám, zjednodušený přepočet podle následující tabulky č. 13.'

function ArticleViiInfo() {
  return (
    <details className="article-info no-print">
      <summary aria-label="Informace k článku VII" title="Zobrazit článek VII">i</summary>
      <div className="article-info-panel" role="note">
        <strong>Článek VII – Zjednodušený přepočet</strong>
        <p>{simplifiedArticleVii}</p>
        <small>Zdroj: Metodický pokyn čj. MZE-64871/2025-15134, čl. VII.</small>
      </div>
    </details>
  )
}

function defaultForAsset(asset: AssetDefinition, previous: CalculationInput): CalculationInput {
  const next = { ...previous, assetId: asset.id, atypicalCost: 0 }
  if (asset.kind === 'pipe') {
    const materials = getPipeMaterials(asset.id)
    return { ...next, dn: asset.id === 'gravity-pipe' ? 300 : 100, length: 1, material: materials.includes('PVC/PE') ? 'PVC/PE' : materials[0], baseMaterial: materials[0], surface: 'n' }
  }
  if (asset.kind === 'noncircular') return { ...next, variant: 'tlamove', dn: 1400, secondaryParameter: 890, length: 1 }
  if (asset.kind === 'wwtp') return { ...next, parameter: 1000 }
  if (asset.kind === 'composite-spring') return { ...next, parameter: 100, dn: 100, length: 1, material: 'PVC/PE', baseMaterial: 'PVC/PE', surface: 'n' }
  if (asset.kind === 'simplified') return { ...next, oldPrice: 1_000_000, simplifiedIndex: 0 }
  if (asset.id === 'water-treatment') return { ...next, parameter: 10, variant: 'single', k1: 1, hardening: false, ozonation: false }
  if (asset.id === 'ground-reservoir' || asset.id === 'tower-reservoir') return { ...next, parameter: 100 }
  if (asset.id === 'water-pump') return { ...next, parameter: 10, variant: 'low' }
  if (asset.id === 'sewer-pump') return { ...next, parameter: 10 }
  return { ...next, parameter: 1 }
}

function NumberField({ label, value, unit, min = 0, max, step = 'any', onChange, hint }: {
  label: string
  value: number
  unit?: string
  min?: number
  max?: number
  step?: number | 'any'
  onChange: (value: number) => void
  hint?: string
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <span className="number-wrap">
        <input type="number" value={Number.isFinite(value) ? value : ''} min={min} max={max} step={step} onChange={(event) => onChange(event.target.valueAsNumber)} />
        {unit && <span className="unit">{unit}</span>}
      </span>
      {hint && <small>{hint}</small>}
    </label>
  )
}

function SelectField({ label, value, onChange, children }: {
  label: string
  value: string | number
  onChange: (value: string) => void
  children: React.ReactNode
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
    </label>
  )
}

function PipeFields({ input, setInput, assetId = input.assetId }: {
  input: CalculationInput
  setInput: React.Dispatch<React.SetStateAction<CalculationInput>>
  assetId?: string
}) {
  const materials = getPipeMaterials(assetId)
  return (
    <>
      <div className="field-grid">
        <NumberField label="DN potrubí" value={input.dn} unit="mm" min={1} step={1} onChange={(dn) => setInput((old) => ({ ...old, dn }))} />
        <NumberField label="Délka" value={input.length} unit="bm" min={0.01} onChange={(length) => setInput((old) => ({ ...old, length }))} />
      </div>
      <div className="field-grid">
        <SelectField label="Materiál" value={input.material} onChange={(material) => setInput((old) => ({ ...old, material, baseMaterial: material === '__other__' ? old.baseMaterial : material }))}>
          {materials.map((material) => <option key={material} value={material}>{material}</option>)}
          <option value="__other__">Jiný materiál…</option>
        </SelectField>
        <SelectField label="Uložení" value={input.surface} onChange={(surface) => setInput((old) => ({ ...old, surface: surface as 'z' | 'n' }))}>
          <option value="z">z – zpevněná plocha</option>
          <option value="n">n – nezpevněná plocha / extravilán</option>
        </SelectField>
      </div>
      {input.material === '__other__' && (
        <div className="subpanel warning-panel">
          <SelectField label="Nejbližší skupina materiálu" value={input.baseMaterial} onChange={(baseMaterial) => setInput((old) => ({ ...old, baseMaterial }))}>
            {materials.map((material) => <option key={material} value={material}>{material}</option>)}
          </SelectField>
          <NumberField label="Koeficient materiálu" value={input.materialCoefficient} min={0.8} step={0.01} onChange={(materialCoefficient) => setInput((old) => ({ ...old, materialCoefficient }))} hint="Povolený interval metodiky: 0,80 až 1,80." />
        </div>
      )}
    </>
  )
}

function DynamicFields({ asset, input, setInput }: {
  asset: AssetDefinition
  input: CalculationInput
  setInput: React.Dispatch<React.SetStateAction<CalculationInput>>
}) {
  if (asset.kind === 'pipe') {
    return (
      <>
        <PipeFields input={input} setInput={setInput} />
        {asset.id === 'water-pipe' && (
          <label className="check-row">
            <input type="checkbox" checked={input.collector} onChange={(event) => setInput((old) => ({ ...old, collector: event.target.checked }))} />
            <span><strong>Potrubí je v kolektoru</strong><small>Použije se 70 % tabulkové hodnoty.</small></span>
          </label>
        )}
        {asset.id === 'water-pipe' && input.dn > 1000 && (
          <details className="advanced">
            <summary>Podchod nebo shybka pod velkou vodotečí</summary>
            <div className="field-grid details-body">
              <NumberField label="Délka podchodu" value={input.crossingLength} unit="bm" onChange={(crossingLength) => setInput((old) => ({ ...old, crossingLength }))} />
              <NumberField label="Jednotková cena" value={input.crossingRate} unit="Kč/bm" max={607600} onChange={(crossingRate) => setInput((old) => ({ ...old, crossingRate }))} hint="Nejvýše 607 600 Kč/bm." />
            </div>
          </details>
        )}
      </>
    )
  }

  if (asset.kind === 'noncircular') {
    const table = methodology.tables.noncircular as { series: Record<string, NumericPoint[]> }
    const points = table.series[input.variant] ?? []
    const exactValue = points.find((point) => point.x === input.dn && point.secondary === input.secondaryParameter)
    return (
      <>
        <SelectField label="Tvar profilu" value={input.variant} onChange={(variant) => {
          const first = table.series[variant]?.[0]
          setInput((old) => ({ ...old, variant, dn: first?.x ?? old.dn, secondaryParameter: first?.secondary ?? old.secondaryParameter }))
        }}>
          {asset.variants?.map((variant) => <option key={variant.value} value={variant.value}>{variant.label}</option>)}
        </SelectField>
        <SelectField label="Standardní profil MZe" value={exactValue?.profile ?? '__custom__'} onChange={(profile) => {
          if (profile === '__custom__') return
          const point = points.find((item) => item.profile === profile)
          if (point) setInput((old) => ({ ...old, dn: point.x, secondaryParameter: point.secondary ?? 0 }))
        }}>
          <option value="__custom__">Vlastní rozměr – pracovní odhad</option>
          {points.map((point) => <option key={point.profile} value={point.profile}>{point.profile} mm</option>)}
        </SelectField>
        <div className="field-grid">
          <NumberField label="První rozměr" value={input.dn} unit="mm" min={1} step={1} onChange={(dn) => setInput((old) => ({ ...old, dn }))} />
          <NumberField label="Druhý rozměr" value={input.secondaryParameter} unit="mm" min={1} step={1} onChange={(secondaryParameter) => setInput((old) => ({ ...old, secondaryParameter }))} />
        </div>
        <NumberField label="Délka stoky" value={input.length} unit="bm" min={0.01} onChange={(length) => setInput((old) => ({ ...old, length }))} />
      </>
    )
  }

  if (asset.kind === 'composite-spring') {
    return (
      <>
        <NumberField label="Užitný objem jímky" value={input.parameter} unit="m³" min={0.01} onChange={(parameter) => setInput((old) => ({ ...old, parameter }))} />
        <div className="section-divider"><span>Navazující potrubí</span></div>
        <PipeFields input={input} setInput={setInput} assetId="water-pipe" />
      </>
    )
  }

  if (asset.kind === 'simplified') {
    const rows = (methodology.tables.simplified as { rows: Array<{ sector: string; label: string; coefficient: number }> }).rows
    return (
      <>
        <SelectField label="Druh majetku" value={input.simplifiedIndex} onChange={(value) => setInput((old) => ({ ...old, simplifiedIndex: Number(value) }))}>
          {rows.map((row, index) => <option key={`${row.sector}-${row.label}`} value={index}>{row.sector}: {row.label}</option>)}
        </SelectField>
        <NumberField label="Původní cena" value={input.oldPrice} unit="Kč" min={1} step={1000} onChange={(oldPrice) => setInput((old) => ({ ...old, oldPrice }))} />
      </>
    )
  }

  if (asset.variants) {
    return (
      <>
        <SelectField label="Varianta" value={input.variant} onChange={(variant) => setInput((old) => ({ ...old, variant }))}>
          {asset.variants.map((variant) => <option key={variant.value} value={variant.value}>{variant.label}</option>)}
        </SelectField>
        <NumberField label={asset.parameterLabel ?? 'Technický parametr'} value={input.parameter} unit={asset.parameterUnit} min={0.01} onChange={(parameter) => setInput((old) => ({ ...old, parameter }))} />
        {asset.id === 'water-treatment' && (
          <div className="subpanel">
            <NumberField label="Základní koeficient K1" value={input.k1} min={0.5} step={0.01} onChange={(k1) => setInput((old) => ({ ...old, k1 }))} hint="Obvykle 1,00; při neúplné technologii 0,50 až 0,95." />
            <label className="check-row compact"><input type="checkbox" checked={input.hardening} onChange={(event) => setInput((old) => ({ ...old, hardening: event.target.checked }))} /><span>Ztvrzování vody (+10 %)</span></label>
            <label className="check-row compact"><input type="checkbox" checked={input.ozonation} onChange={(event) => setInput((old) => ({ ...old, ozonation: event.target.checked }))} /><span>Ozonizace (+10 %)</span></label>
          </div>
        )}
      </>
    )
  }

  if (asset.kind === 'table-cu' || asset.kind === 'wwtp') {
    return <NumberField label={asset.parameterLabel ?? 'Technický parametr'} value={input.parameter} unit={asset.parameterUnit} min={0.01} onChange={(parameter) => setInput((old) => ({ ...old, parameter }))} />
  }

  if (asset.kind === 'fixed') {
    const fixed = methodology.fixedAssets.find((item) => item.id === asset.fixedId)
    return fixed?.kind === 'rate'
      ? <NumberField label={fixed.parameter ?? 'Technický parametr'} value={input.parameter} unit={fixed.parameterUnit} min={0.01} onChange={(parameter) => setInput((old) => ({ ...old, parameter }))} />
      : <div className="fixed-note"><strong>Pevná sazba za jeden objekt</strong><span>{fixed?.value.toLocaleString('cs-CZ')} Kč s DPH před použitím K5.</span></div>
  }

  return null
}

function ResultCard({ result, error, input, onCopyStatus }: {
  result: CalculationResult | null
  error: string
  input: CalculationInput
  onCopyStatus: (message: string) => void
}) {
  if (error) return <section className="card result-card empty-result" aria-live="polite"><div className="empty-icon">!</div><h2>Výsledek zatím nelze určit</h2><p>{error}</p></section>
  if (!result) return <section className="card result-card empty-result"><div className="empty-icon">∑</div><h2>Zadejte parametry majetku</h2><p>Výsledek se zobrazí automaticky.</p></section>
  return (
    <section className="card result-card" aria-live="polite">
      <div className="result-topline">
        <span className={`status status-${result.method}`}>{result.methodLabel}</span>
        <span className="result-time">Aktuální náhled</span>
      </div>
      <p className="eyebrow">Celková cena objektu s 21 % DPH</p>
      <div className="main-price">{formatMoney(result.totalGross)}</div>
      <div className="net-price">Bez DPH: <strong>{formatMoney(result.totalNet)}</strong></div>
      <div className="indicator-box">
        <span>{result.indicatorLabel}</span>
        <strong>{formatIndicator(result.indicatorGross, result.indicatorUnit)}</strong>
        <small>Bez DPH: {formatIndicator(result.indicatorNet, result.indicatorUnit)}</small>
      </div>
      {result.breakdown.length > 0 && (
        <div className="breakdown">
          <h3>Členění ceny</h3>
          {result.breakdown.map((item) => <div className="breakdown-row" key={item.label}><span>{item.label}</span><strong>{formatMoney(item.gross)}</strong></div>)}
        </div>
      )}
      {result.warnings.length > 0 && (
        <div className="warnings">
          {result.warnings.map((warning) => (
            <div className="warning-row" key={warning}>
              <p><strong>Upozornění:</strong> {warning}</p>
              {result.assetId === 'simplified' && warning.includes('článku VII') && <ArticleViiInfo />}
            </div>
          ))}
        </div>
      )}
      <details className="audit" open>
        <summary>Jak byl výsledek vypočten</summary>
        <div className="audit-body">
          <p><strong>Vstupy:</strong> {result.inputSummary}</p>
          <p><strong>Vzorec:</strong> {result.formula}</p>
          {result.adjustments.length > 0 && <p><strong>Úpravy:</strong> {result.adjustments.join('; ')}</p>}
          {result.sourcePoints.length > 0 && <p><strong>Zdrojové body:</strong> {result.sourcePoints.map((point) => `${point.x} → ${point.value.toLocaleString('cs-CZ')}`).join('; ')}</p>}
          <p><strong>Zdroj:</strong> {result.sourceLabel}</p>
        </div>
      </details>
      <div className="action-grid no-print">
        <button type="button" className="button secondary" onClick={async () => { await copyResult(result); onCopyStatus('Výsledek byl zkopírován.') }}>Kopírovat</button>
        <button type="button" className="button secondary" onClick={() => window.print()}>Tisk / PDF</button>
        <button type="button" className="button secondary" onClick={() => exportCsv(result)}>CSV</button>
        <button type="button" className="button secondary" onClick={async () => { await exportXlsx(input, result); onCopyStatus('Soubor XLSX byl vytvořen.') }}>XLSX</button>
      </div>
    </section>
  )
}

function HistoryList({ history, onRestore, onRemove, onClear }: {
  history: HistoryEntry[]
  onRestore: (entry: HistoryEntry) => void
  onRemove: (id: string) => void
  onClear: () => void
}) {
  return (
    <section className="card history-card no-print">
      <div className="card-heading"><div><p className="eyebrow">Pouze v tomto prohlížeči</p><h2>Posledních 10 výpočtů</h2></div>{history.length > 0 && <button type="button" className="text-button" onClick={onClear}>Vymazat vše</button>}</div>
      {history.length === 0 ? <p className="muted">Zatím zde není žádný uložený výpočet.</p> : (
        <ol className="history-list">
          {history.map((entry) => (
            <li key={entry.id}>
              <button type="button" className="history-main" onClick={() => onRestore(entry)}>
                <span className="history-title">{entry.result.assetLabel}</span>
                <span className="history-summary">{entry.result.inputSummary}</span>
                <span className="history-meta"><strong>{formatMoney(entry.result.totalGross)}</strong><time>{new Date(entry.createdAt).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}</time></span>
                {isHistoryOutdated(entry) && <span className="outdated">Starší verze dat – po otevření se přepočítá</span>}
              </button>
              <button type="button" className="history-remove" aria-label={`Odstranit výpočet ${entry.result.assetLabel}`} onClick={() => onRemove(entry.id)}>×</button>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

type SourceTableInfo = {
  table: string
  title: string
  variant?: string
  parameter: string
  parameterUnit: string
  valueUnit: string
  note?: string
}

type PointSourceTable = {
  points: NumericPoint[]
}

type SeriesSourceTable = {
  series: Record<string, NumericPoint[]>
}

const sourceTableInfo: Record<string, SourceTableInfo> = {
  waterTreatmentSingle: {
    table: '1',
    title: 'Měrný cenový ukazatel typového objektu úpravny vody',
    variant: 'jednostupňová úprava',
    parameter: 'výkon úpravny vody Q',
    parameterUnit: 'l×s⁻¹',
    valueUnit: 'Cu v tis. Kč',
    note: 'Při neúplné technologii se použije koeficient K1; ztvrzování vody a ozonizace se připočítávají podle metodického pokynu.',
  },
  waterTreatmentDouble: {
    table: '1',
    title: 'Měrný cenový ukazatel typového objektu úpravny vody',
    variant: 'dvoustupňová úprava',
    parameter: 'výkon úpravny vody Q',
    parameterUnit: 'l×s⁻¹',
    valueUnit: 'Cu v tis. Kč',
    note: 'Při neúplné technologii se použije koeficient K1; ztvrzování vody a ozonizace se připočítávají podle metodického pokynu.',
  },
  groundReservoir: {
    table: '2',
    title: 'Cenový ukazatel typového objektu zemního vodojemu',
    parameter: 'užitný objem vodojemu',
    parameterUnit: 'm³',
    valueUnit: 'Cu v tis. Kč',
  },
  towerReservoir: {
    table: '3',
    title: 'Cenový ukazatel typového objektu věžového vodojemu',
    parameter: 'užitný objem vodojemu',
    parameterUnit: 'm³',
    valueUnit: 'Cu v tis. Kč',
  },
  waterPumpLowHead: {
    table: '4',
    title: 'Cenový ukazatel typového objektu čerpací stanice do výtlaku H = 60 m',
    parameter: 'kapacita čerpací stanice Qč',
    parameterUnit: 'l×s⁻¹',
    valueUnit: 'Cu v tis. Kč',
    note: 'Při kapacitě nad 50 l×s⁻¹ se cenový ukazatel stanoví extrapolací minimálně ze tří posledních uvedených údajů.',
  },
  waterPumpHighHead: {
    table: '5',
    title: 'Cenový ukazatel typového objektu čerpací stanice s výtlakem nad H = 60 m',
    parameter: 'kapacita čerpací stanice Qč',
    parameterUnit: 'l×s⁻¹',
    valueUnit: 'Cu v tis. Kč',
  },
  waterPipe: {
    table: '6',
    title: 'Měrný cenový ukazatel typového objektu vodovodní řady',
    parameter: 'DN',
    parameterUnit: 'mm',
    valueUnit: 'Cmu v Kč/bm potrubí',
    note: 'z = uložení ve zpevněné ploše; n = uložení v nezpevněné ploše nebo extravilánu.',
  },
  wwtpSmall: {
    table: '7a',
    title: 'Měrný cenový ukazatel typového objektu mechanicko-biologické ČOV',
    variant: 'do 4 999 EO',
    parameter: 'velikost ČOV',
    parameterUnit: 'EO',
    valueUnit: 'Cmu v Kč/EO',
  },
  wwtpLarge: {
    table: '7b',
    title: 'Měrný cenový ukazatel typového objektu mechanicko-biologické ČOV',
    variant: 'od 5 000 EO',
    parameter: 'velikost ČOV',
    parameterUnit: 'EO',
    valueUnit: 'Cmu v Kč/EO',
    note: 'Od velikosti 600 000 EO se použije poslední pevný měrný cenový ukazatel.',
  },
  sewerPump: {
    table: '8',
    title: 'Cenový ukazatel typového objektu kanalizační čerpací stanice',
    parameter: 'kapacita čerpací stanice Qč',
    parameterUnit: 'l×s⁻¹',
    valueUnit: 'Cu v tis. Kč',
  },
  gravityPipe: {
    table: '9',
    title: 'Měrný cenový ukazatel typového objektu stoky kruhové',
    parameter: 'DN',
    parameterUnit: 'mm',
    valueUnit: 'Cmu v Kč/bm potrubí',
    note: 'z = uložení ve zpevněné ploše; n = uložení v nezpevněné ploše nebo extravilánu.',
  },
  noncircular: {
    table: '10',
    title: 'Měrný cenový ukazatel tlamových a vejčitých stok',
    parameter: 'profil stoky',
    parameterUnit: 'mm',
    valueUnit: 'Cmu v Kč/bm potrubí',
    note: 'Vlastní nestandardní profil je v kalkulátoru označen jako pracovní odhad podle prvního rozměru.',
  },
  pressurePipe: {
    table: '11',
    title: 'Měrný cenový ukazatel tlakové a podtlakové stoky',
    parameter: 'DN',
    parameterUnit: 'mm',
    valueUnit: 'Cmu v Kč/bm potrubí',
    note: 'z = uložení ve zpevněné ploše; n = uložení v nezpevněné ploše nebo extravilánu.',
  },
  simplified: {
    table: '13',
    title: 'Koeficienty zjednodušeného přepočtu pořizovací ceny',
    parameter: 'druh majetku',
    parameterUnit: '–',
    valueUnit: 'koeficient',
    note: 'Zjednodušený přepočet lze použít pouze při splnění podmínek uvedených v článku VII metodického pokynu.',
  },
}

const sourceOrder = [
  'waterTreatmentSingle', 'waterTreatmentDouble', 'groundReservoir', 'towerReservoir',
  'waterPumpLowHead', 'waterPumpHighHead', 'waterPipe', 'wwtpSmall', 'wwtpLarge',
  'sewerPump', 'gravityPipe', 'noncircular', 'pressurePipe',
]

const formatSourceNumber = (value?: number) => value === undefined ? '–' : value.toLocaleString('cs-CZ')

function MethodSheetHeader({ info }: { info: SourceTableInfo }) {
  return (
    <header className="method-sheet-header">
      <h3>{info.title}</h3>
      {info.variant && <p>{info.variant}</p>}
      <span>Tabulka č. {info.table}</span>
    </header>
  )
}

function MethodSheetNote({ children }: { children?: React.ReactNode }) {
  return (
    <div className="method-sheet-notes">
      <p>Hodnoty jsou převzaty z metodického pokynu {methodology.meta.methodology} a zahrnují 21 % DPH.</p>
      {children && <p>{children}</p>}
    </div>
  )
}

function PointMethodTable({ tableKey, raw }: { tableKey: string, raw: PointSourceTable }) {
  const info = sourceTableInfo[tableKey]
  const hasBreakdown = raw.points.some((point) => point.building !== undefined || point.technology !== undefined)
  return (
    <div className="method-sheet">
      <MethodSheetHeader info={info} />
      <div className="method-table-scroll">
        <table className="method-table">
          <thead>
            <tr>
              <th>{info.parameter}</th>
              <th>{hasBreakdown ? 'cenový ukazatel celkem' : 'cenový ukazatel'}</th>
              {hasBreakdown && <th>stavební část</th>}
              {hasBreakdown && <th>technologická část</th>}
            </tr>
            <tr className="unit-row">
              <th>{info.parameterUnit}</th>
              <th>{info.valueUnit}</th>
              {hasBreakdown && <th>{info.valueUnit}</th>}
              {hasBreakdown && <th>{info.valueUnit}</th>}
            </tr>
          </thead>
          <tbody>
            {raw.points.map((point) => (
              <tr key={point.x}>
                <td>{formatSourceNumber(point.x)}</td>
                <td>{formatSourceNumber(point.total ?? point.value)}</td>
                {hasBreakdown && <td>{formatSourceNumber(point.building)}</td>}
                {hasBreakdown && <td>{formatSourceNumber(point.technology)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MethodSheetNote>{info.note}</MethodSheetNote>
    </div>
  )
}

function PipeMethodTable({ tableKey, raw }: { tableKey: string, raw: SeriesSourceTable }) {
  const info = sourceTableInfo[tableKey]
  const seriesEntries = Object.entries(raw.series)
  const materials = Array.from(new Set(seriesEntries.map(([name]) => name.split('|')[1])))
  const diameters = Array.from(new Set(seriesEntries.flatMap(([, points]) => points.map((point) => point.x)))).sort((a, b) => a - b)
  const values = new Map(seriesEntries.flatMap(([name, points]) => {
    const [, material, surface] = name.split('|')
    return points.map((point) => [`${material}|${surface}|${point.x}`, point.value] as const)
  }))
  return (
    <div className="method-sheet method-sheet-wide">
      <MethodSheetHeader info={info} />
      <div className="method-table-unit">{info.valueUnit}</div>
      <div className="method-table-scroll">
        <table className="method-table pipe-method-table">
          <thead>
            <tr>
              <th rowSpan={2}>DN<br /><span>{info.parameterUnit}</span></th>
              {materials.map((material) => <th key={material} colSpan={2}>{material}</th>)}
            </tr>
            <tr className="unit-row">
              {materials.flatMap((material) => [
                <th key={`${material}-z`}>z</th>,
                <th key={`${material}-n`}>n</th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {diameters.map((dn) => (
              <tr key={dn}>
                <td>{formatSourceNumber(dn)}</td>
                {materials.flatMap((material) => [
                  <td key={`${material}-z-${dn}`}>{formatSourceNumber(values.get(`${material}|z|${dn}`))}</td>,
                  <td key={`${material}-n-${dn}`}>{formatSourceNumber(values.get(`${material}|n|${dn}`))}</td>,
                ])}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MethodSheetNote>{info.note}</MethodSheetNote>
    </div>
  )
}

function NoncircularMethodTable({ raw }: { raw: SeriesSourceTable }) {
  const info = sourceTableInfo.noncircular
  return (
    <div className="method-sheet">
      <MethodSheetHeader info={info} />
      <div className="method-table-scroll">
        <table className="method-table">
          <thead>
            <tr><th>tvar stoky</th><th>{info.parameter}</th><th>cenový ukazatel</th></tr>
            <tr className="unit-row"><th>–</th><th>{info.parameterUnit}</th><th>{info.valueUnit}</th></tr>
          </thead>
          <tbody>
            {Object.entries(raw.series).flatMap(([shape, points]) => points.map((point, index) => (
              <tr key={`${shape}-${point.profile}`}>
                {index === 0 && <td rowSpan={points.length} className="row-group">{shape === 'tlamove' ? 'tlamový profil' : 'vejčitý profil'}</td>}
                <td>{point.profile}</td>
                <td>{formatSourceNumber(point.value)}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      <MethodSheetNote>{info.note}</MethodSheetNote>
    </div>
  )
}

function SimplifiedMethodTable() {
  const info = sourceTableInfo.simplified
  const rows = (methodology.tables.simplified as { rows: Array<{ sector: string; label: string; coefficient: number }> }).rows
  return (
    <div className="method-sheet">
      <MethodSheetHeader info={info} />
      <div className="method-table-scroll">
        <table className="method-table">
          <thead><tr><th>oblast</th><th>{info.parameter}</th><th>{info.valueUnit}</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={`${row.sector}-${row.label}`}><td>{row.sector}</td><td className="text-cell">{row.label}</td><td>{row.coefficient.toFixed(2).replace('.', ',')}</td></tr>)}</tbody>
        </table>
      </div>
      <MethodSheetNote>{info.note}</MethodSheetNote>
    </div>
  )
}

function K5MethodTable() {
  return (
    <div className="method-sheet">
      <MethodSheetHeader info={{ table: '12', title: 'Polohový koeficient podle velikosti obce', parameter: 'velikost obce', parameterUnit: '–', valueUnit: 'K5' }} />
      <div className="method-table-scroll">
        <table className="method-table">
          <thead><tr><th>skupina obcí</th><th>polohový koeficient K5</th></tr></thead>
          <tbody>{methodology.k5.map((row) => <tr key={row.id}><td className="text-cell">{row.label}</td><td>{row.value.toFixed(2).replace('.', ',')}</td></tr>)}</tbody>
        </table>
      </div>
      <MethodSheetNote>Výchozí hodnota kalkulátoru je K5 = 1,00.</MethodSheetNote>
    </div>
  )
}

function FixedRatesMethodTable() {
  return (
    <div className="method-sheet">
      <header className="method-sheet-header">
        <h3>Pevné sazby a měrné cenové ukazatele ostatních objektů</h3>
        <span>Články IV a V</span>
      </header>
      <div className="method-table-scroll">
        <table className="method-table">
          <thead><tr><th>majetek</th><th>cenový ukazatel</th><th>zdroj</th></tr></thead>
          <tbody>{methodology.fixedAssets.map((row) => <tr key={row.id}><td className="text-cell">{row.label}</td><td>{row.value.toLocaleString('cs-CZ')} {row.unit}</td><td>{row.source}</td></tr>)}</tbody>
        </table>
      </div>
      <MethodSheetNote />
    </div>
  )
}

function SourceDetails({ tableKey }: { tableKey: string }) {
  const info = sourceTableInfo[tableKey]
  const raw = methodology.tables[tableKey]
  return (
    <details className="source-details">
      <summary>
        <span className="source-table-number">Tabulka {info.table}</span>
        <span>{info.title}{info.variant ? ` – ${info.variant}` : ''}</span>
        <span className="source-toggle" aria-hidden="true">+</span>
      </summary>
      {tableKey === 'noncircular'
        ? <NoncircularMethodTable raw={raw as SeriesSourceTable} />
        : 'series' in (raw as object)
          ? <PipeMethodTable tableKey={tableKey} raw={raw as SeriesSourceTable} />
          : <PointMethodTable tableKey={tableKey} raw={raw as PointSourceTable} />}
    </details>
  )
}

function SourceData() {
  return (
    <section className="source-section no-print" id="zdrojova-data">
      <div className="source-intro">
        <p className="eyebrow">Auditovatelný datový základ</p>
        <h2>Tabulky ve vzhledu metodického pokynu</h2>
        <p>Každá tabulka zachovává vlastní názvy, jednotky, členění a zdrojové body. Otevřete ji kliknutím na její číslo nebo název.</p>
      </div>
      <div className="excel-download">
        <div>
          <p className="eyebrow">Podklad pro práci mimo web</p>
          <h3>PFO tabulky s vlastním kalkulátorem</h3>
          <p>Vodovodní a kanalizační potrubí i čerpací stanice ve vzhledu metodického pokynu. Na každém výpočtovém listu lze zadat vlastní DN nebo Qč; výpočet zachovává desetinnou přesnost a zaokrouhluje až konečnou cenu.</p>
          <span>Metodika {methodology.meta.methodology} · data {methodology.meta.dataVersion}</span>
        </div>
        <div className="download-actions">
          <a className="button primary excel-download-button" href="/downloads/PFO_Cmu_MZe_potrubi_a_cerpaci_stanice_2026_kalkulator_presne_hodnoty.xlsx" download>Stáhnout Excel s kalkulátorem</a>
        </div>
      </div>
      <div className="source-list">
        {sourceOrder.map((tableKey) => <SourceDetails tableKey={tableKey} key={tableKey} />)}
        <details className="source-details">
          <summary><span className="source-table-number">Tabulka 12</span><span>Polohový koeficient podle velikosti obce</span><span className="source-toggle" aria-hidden="true">+</span></summary>
          <K5MethodTable />
        </details>
        <details className="source-details">
          <summary><span className="source-table-number">Tabulka 13</span><span>Koeficienty zjednodušeného přepočtu pořizovací ceny</span><span className="source-toggle" aria-hidden="true">+</span></summary>
          <SimplifiedMethodTable />
        </details>
        <details className="source-details">
          <summary><span className="source-table-number">Čl. IV a V</span><span>Pevné sazby a ostatní měrné cenové ukazatele</span><span className="source-toggle" aria-hidden="true">+</span></summary>
          <FixedRatesMethodTable />
        </details>
      </div>
    </section>
  )
}

export default function App() {
  const [input, setInput] = useState<CalculationInput>({ ...defaultInputValues })
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [message, setMessage] = useState('')
  const asset = assetById.get(input.assetId) ?? assets[0]

  useEffect(() => setHistory(refreshOutdatedHistory()), [])
  useEffect(() => {
    if (!message) return
    const timeout = window.setTimeout(() => setMessage(''), 2500)
    return () => window.clearTimeout(timeout)
  }, [message])

  const preview = useMemo(() => {
    try { return { result: calculate(input), error: '' } }
    catch (error) { return { result: null, error: error instanceof Error ? error.message : 'Výpočet se nezdařil.' } }
  }, [input])

  const groupedAssets = useMemo(() => {
    const byGroup = new Map<string, AssetDefinition[]>()
    assets.filter((item) => item.sector === asset.sector).forEach((item) => byGroup.set(item.group, [...(byGroup.get(item.group) ?? []), item]))
    return byGroup
  }, [asset.sector])

  const changeSector = (sector: Sector) => {
    const first = assets.find((item) => item.sector === sector)!
    setInput((old) => defaultForAsset(first, old))
  }

  const saveCurrent = () => {
    if (!preview.result) return
    setHistory(addHistory(input, preview.result))
    setMessage('Výpočet byl uložen do místní historie.')
  }

  return (
    <>
      <header className="hero">
        <nav className="topbar"><a className="brand" href="#top" aria-label="Cenové ukazatele VaK"><span className="brand-mark"><img src="/logo-vak.png" alt="" /></span><span>Cenové ukazatele</span></a><a href="#zdrojova-data">Zdrojová data</a></nav>
        <div className="hero-content" id="top">
          <div>
            <span className="method-chip">Metodický pokyn čj. {methodology.meta.methodology}</span>
            <h1>Cenové ukazatele vodovodů a kanalizací</h1>
            <p>Zadejte technické parametry a okamžitě získáte dohledatelný měrný ukazatel i celkovou cenu objektu.</p>
          </div>
        </div>
      </header>

      <main>
        <div className="disclaimer"><strong>Neoficiální pracovní kalkulátor.</strong> Nenahrazuje MPVaK ani stanovisko Ministerstva zemědělství. Pracovní dopočty jsou vždy označeny.</div>
        <div className="workspace">
          <section className="card calculator-card no-print">
            <div className="card-heading"><div><p className="eyebrow">Krok 1</p><h2>Vyberte majetek</h2></div><span className="data-version">Data {methodology.meta.dataVersion}</span></div>
            <div className="sector-tabs" role="tablist" aria-label="Oblast majetku">
              {sectors.map((sector) => <button key={sector} role="tab" aria-selected={asset.sector === sector} className={asset.sector === sector ? 'active' : ''} onClick={() => changeSector(sector)}>{sector}</button>)}
            </div>
            <label className="field asset-select"><span className="field-label">Druh majetku</span><select value={asset.id} onChange={(event) => setInput((old) => defaultForAsset(assetById.get(event.target.value)!, old))}>{[...groupedAssets].map(([group, items]) => <optgroup key={group} label={group}>{items.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</optgroup>)}</select></label>
            <p className="asset-description">{asset.description}</p>
            <div className="section-divider"><span>Krok 2 · Technické parametry</span></div>
            <DynamicFields asset={asset} input={input} setInput={setInput} />
            {asset.kind !== 'simplified' && (
              <>
                <div className="section-divider"><span>Krok 3 · Umístění a úpravy</span></div>
                <SelectField label="Velikost obce – polohový koeficient K5" value={input.k5Id} onChange={(value) => setInput((old) => ({ ...old, k5Id: Number(value) }))}>
                  {methodology.k5.map((row) => <option key={row.id} value={row.id}>{row.label} · K5 {row.value.toFixed(2).replace('.', ',')}</option>)}
                </SelectField>
                {!['composite-spring'].includes(asset.kind) && <details className="advanced"><summary>Atypické náklady nezahrnuté v ukazateli</summary><div className="details-body"><NumberField label="Další náklady s DPH" value={input.atypicalCost} unit="Kč" min={0} step={1000} onChange={(atypicalCost) => setInput((old) => ({ ...old, atypicalCost }))} /></div></details>}
              </>
            )}
            <button type="button" className="button primary save-button" disabled={!preview.result} onClick={saveCurrent}>Uložit tento výpočet do historie</button>
            <p className="privacy-note">Historie obsahuje nejvýše 10 výpočtů a ukládá se pouze v tomto prohlížeči.</p>
          </section>
          <ResultCard result={preview.result} error={preview.error} input={input} onCopyStatus={setMessage} />
        </div>
        <HistoryList history={history} onRestore={(entry) => { setInput(entry.input); setMessage(isHistoryOutdated(entry) ? 'Výpočet byl obnoven a přepočten podle aktuálních dat.' : 'Výpočet byl obnoven.'); window.scrollTo({ top: 0, behavior: 'smooth' }) }} onRemove={(id) => setHistory(removeHistory(id))} onClear={() => setHistory(clearHistory())} />
        <SourceData />
      </main>
      <footer><div><strong>Cenové ukazatele VaK</strong><span>Metodický pokyn {methodology.meta.methodology} ze dne {methodology.meta.sourceDate}</span></div><p>{methodology.meta.disclaimer}</p></footer>
      {message && <div className="toast" role="status">{message}</div>}
    </>
  )
}
